import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrivalChart } from "@/components/pages/dashboard/components/charts/dashboard_charts/ArrivalChart";
import {
  Activity,
  ArrowDown,
  ArrowLeftToLine,
  ArrowUp,
  Bed,
  Briefcase,
  ClipboardList,
  LayoutDashboard,
  LineChart,
  MessageCircle,
  Plane,
  Settings2,
} from "lucide-react";
import Badge from "@/components/pages/dashboard/components/Badge";
import { DashboardGreeting } from "@/components/pages/dashboard/components/DashboardGreeting";
import { DateTime } from "@/components/common/DateTime";
import { DepartureChart } from "@/components/pages/dashboard/components/charts/dashboard_charts/DepartureChart";
import { InHouseChart } from "@/components/pages/dashboard/components/charts/dashboard_charts/InHouseChart";
import { DashboardChatsContainer } from "@/components/pages/dashboard/components/DashboardChatsContainer";
import { useDashboardGuestChatsCount } from "@/components/pages/dashboard/hooks/useDashboardGuestChatsCount";
import { DashboardAnalytics } from "@/components/pages/dashboard/components/DashboardAnalytics";
import { useTodaySettlement } from "@/components/pages/settlement/hooks/useTodaySettlement";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { Role } from "@/types/user";
import { useNavigate } from "react-router-dom";
import { DashboardRequestsContainer } from "@/components/pages/dashboard/components/DashboardRequestsContainer";
import { useDashboardGuestRequestMetrics } from "@/components/pages/dashboard/hooks/useDashboardGuestRequestMetrics";
import {
  useDashboardKeyMetrics,
  type DashboardKeyMetricsState,
} from "@/components/pages/dashboard/hooks/useDashboardKeyMetrics";
import { useDashboardSettlementHourlyCharts } from "@/components/pages/dashboard/hooks/useDashboardSettlementHourlyCharts";
import { Sidebar as SidebarPanel } from "@/components/ui/sidebar";
import { GuestExperienceSidebar } from "@/components/pages/dashboard/components/GuestExperienceSidebar";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { ActivityLogContainer } from "./components/ActivityLogContainer";
import { DashboardVidgetContainer } from "./components/DashboardVidgetContainer";
import { DashboardKeyMetrics } from "./components/DashboardKeyMetrics";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PeriodType } from "./components/charts/dashboard_charts/OccupancyMetricsChart";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { hasCorePlatformAccess } from "@/utils/accessControl";
import { cn } from "@/lib/utils";
import { useHotel } from "@/hooks/useHotel";
import { useCurrentSystemNotificationSetting } from "@/hooks/useNotifications";

const dashboardChartVidgetClassName =
  "h-full min-h-0 w-full gap-2 px-4 pt-4 pb-px";

function getTopRowColsClass(count: number): string {
  if (count === 3) {
    return "grid-cols-3";
  }
  if (count === 2) {
    return "grid-cols-2";
  }
  return "grid-cols-1";
}

interface BottomRowColSpans {
  activityLog: string;
  analytics: string;
  keyMetrics: string;
}

function getBottomRowColSpans(
  showActivityLog: boolean,
  showAnalytics: boolean,
  showKeyMetrics: boolean,
): BottomRowColSpans {
  if (showActivityLog && showAnalytics && showKeyMetrics) {
    return {
      activityLog: "col-span-3",
      analytics: "col-span-5",
      keyMetrics: "col-span-2",
    };
  }
  if (showActivityLog && showAnalytics) {
    return {
      activityLog: "col-span-4",
      analytics: "col-span-6",
      keyMetrics: "",
    };
  }
  if (showActivityLog && showKeyMetrics) {
    return {
      activityLog: "col-span-8",
      analytics: "",
      keyMetrics: "col-span-2",
    };
  }
  if (showAnalytics && showKeyMetrics) {
    return {
      activityLog: "",
      analytics: "col-span-7",
      keyMetrics: "col-span-3",
    };
  }
  return {
    activityLog: showActivityLog ? "col-span-10" : "",
    analytics: showAnalytics ? "col-span-10" : "",
    keyMetrics: showKeyMetrics ? "col-span-10" : "",
  };
}

type TopDashboardWidgetId = "arrivals" | "inHouse" | "departures";
type MiddleDashboardWidgetId = "chats" | "requests";
type BottomDashboardWidgetId = "activityLog" | "analytics" | "keyMetrics";
type DashboardWidgetId =
  | TopDashboardWidgetId
  | MiddleDashboardWidgetId
  | BottomDashboardWidgetId;

interface DashboardWidgetPreferences {
  topOrder: DashboardWidgetId[];
  middleOrder: DashboardWidgetId[];
  bottomOrder: DashboardWidgetId[];
  hidden: Partial<Record<DashboardWidgetId, boolean>>;
}

const defaultDashboardWidgetPreferences: DashboardWidgetPreferences = {
  topOrder: ["arrivals", "inHouse", "departures"],
  middleOrder: ["chats", "requests"],
  bottomOrder: ["activityLog", "analytics", "keyMetrics"],
  hidden: {},
};

const topWidgetIds: DashboardWidgetId[] = ["arrivals", "inHouse", "departures"];
const middleWidgetIds: DashboardWidgetId[] = ["chats", "requests"];
const bottomWidgetIds: DashboardWidgetId[] = [
  "activityLog",
  "analytics",
  "keyMetrics",
];

const dashboardWidgetLabels: Record<DashboardWidgetId, string> = {
  arrivals: "Arrivals",
  inHouse: "In-House",
  departures: "Departures",
  chats: "Chats",
  requests: "Requests",
  activityLog: "Activity Log",
  analytics: "Analytics",
  keyMetrics: "Key Metrics",
};

function normalizeWidgetOrder(
  savedOrder: unknown,
  defaultOrder: DashboardWidgetId[],
): DashboardWidgetId[] {
  if (!Array.isArray(savedOrder)) {
    return defaultOrder;
  }

  const allowed = new Set<string>(defaultOrder);
  const normalized = savedOrder.filter(
    (id): id is DashboardWidgetId => typeof id === "string" && allowed.has(id),
  );
  return [
    ...normalized,
    ...defaultOrder.filter((id) => !normalized.includes(id)),
  ];
}

function parseDashboardWidgetPreferences(
  raw: string | null,
): DashboardWidgetPreferences {
  if (!raw) {
    return defaultDashboardWidgetPreferences;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<DashboardWidgetPreferences>;
    return {
      topOrder: normalizeWidgetOrder(parsed.topOrder, topWidgetIds),
      middleOrder: normalizeWidgetOrder(parsed.middleOrder, middleWidgetIds),
      bottomOrder: normalizeWidgetOrder(parsed.bottomOrder, bottomWidgetIds),
      hidden:
        parsed.hidden && typeof parsed.hidden === "object" ? parsed.hidden : {},
    };
  } catch {
    return defaultDashboardWidgetPreferences;
  }
}

function moveWidget(
  order: DashboardWidgetId[],
  id: DashboardWidgetId,
  direction: "up" | "down",
): DashboardWidgetId[] {
  const index = order.indexOf(id);
  const nextIndex = direction === "up" ? index - 1 : index + 1;
  if (index < 0 || nextIndex < 0 || nextIndex >= order.length) {
    return order;
  }

  const next = [...order];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
}

function useDashboardWidgetPreferences(storageKey: string) {
  const [preferences, setPreferences] = useState<DashboardWidgetPreferences>(
    defaultDashboardWidgetPreferences,
  );

  useEffect(() => {
    setPreferences(
      parseDashboardWidgetPreferences(localStorage.getItem(storageKey)),
    );
  }, [storageKey]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(preferences));
  }, [preferences, storageKey]);

  return [preferences, setPreferences] as const;
}

interface DashboardWidgetCustomizerProps {
  preferences: DashboardWidgetPreferences;
  availableWidgetIds: Set<DashboardWidgetId>;
  onPreferencesChange: React.Dispatch<
    React.SetStateAction<DashboardWidgetPreferences>
  >;
}

function DashboardWidgetCustomizer({
  preferences,
  availableWidgetIds,
  onPreferencesChange,
}: DashboardWidgetCustomizerProps) {
  const renderRow = (
    id: DashboardWidgetId,
    orderKey: "topOrder" | "middleOrder" | "bottomOrder",
  ) => {
    const order = preferences[orderKey];
    const index = order.indexOf(id);
    const visible = preferences.hidden[id] !== true;

    return (
      <div
        key={id}
        className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 px-2 py-1.5"
      >
        <span className="min-w-0 truncate text-xs text-zinc-700">
          {dashboardWidgetLabels[id]}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <Switch
            checked={visible}
            onCheckedChange={(checked) =>
              onPreferencesChange((current) => ({
                ...current,
                hidden: {
                  ...current.hidden,
                  [id]: !checked,
                },
              }))
            }
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={index <= 0}
            onClick={() =>
              onPreferencesChange((current) => ({
                ...current,
                [orderKey]: moveWidget(current[orderKey], id, "up"),
              }))
            }
            aria-label={`Move ${dashboardWidgetLabels[id]} up`}
          >
            <ArrowUp className="size-3.5" aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={index < 0 || index >= order.length - 1}
            onClick={() =>
              onPreferencesChange((current) => ({
                ...current,
                [orderKey]: moveWidget(current[orderKey], id, "down"),
              }))
            }
            aria-label={`Move ${dashboardWidgetLabels[id]} down`}
          >
            <ArrowDown className="size-3.5" aria-hidden />
          </Button>
        </div>
      </div>
    );
  };

  const renderGroup = (
    title: string,
    orderKey: "topOrder" | "middleOrder" | "bottomOrder",
  ) => {
    const ids = preferences[orderKey].filter((id) =>
      availableWidgetIds.has(id),
    );
    if (ids.length === 0) {
      return null;
    }

    return (
      <div className="space-y-2">
        <p className="text-xs font-medium text-zinc-500">{title}</p>
        <div className="space-y-1.5">
          {ids.map((id) => renderRow(id, orderKey))}
        </div>
      </div>
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-2">
          <Settings2 className="size-4" aria-hidden />
          Customize dashboard
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-4">
        <div>
          <p className="text-sm font-medium text-zinc-900">Dashboard widgets</p>
          <p className="mt-1 text-xs text-zinc-500">
            Choose visible widgets and adjust their order.
          </p>
        </div>
        {renderGroup("Summary", "topOrder")}
        {renderGroup("Operations", "middleOrder")}
        {renderGroup("Insights", "bottomOrder")}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => onPreferencesChange(defaultDashboardWidgetPreferences)}
        >
          Reset layout
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export const DashboardPage: React.FC = () => {
  const [guestExperienceOpen, setGuestExperienceOpen] = useState(true);
  const [analyticsPeriod, setAnalyticsPeriod] = useState<PeriodType>("7d");
  const { arrivals, departures, inHouse } = useTodaySettlement();
  const { userProfile } = useAuthenticatedUser();
  const { hotelId } = useHotel();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { capabilities, hasFeedbacksAccess } = useAccessCapabilities();
  const { data: newChatMessagePolicy } = useCurrentSystemNotificationSetting(
    "NEW_CHAT_MESSAGE",
    capabilities.canUseGuestCommunication,
  );
  const isNewChatMessageNotificationEnabled =
    newChatMessagePolicy?.enabled ?? true;
  const dashboardGuestChatsCount = useDashboardGuestChatsCount(
    capabilities.canUseGuestCommunication &&
      isNewChatMessageNotificationEnabled,
  );
  const showDashboardCoreWidgets = hasCorePlatformAccess(
    userProfile,
    "dashboard",
  );
  const isActivityLogOnly = !showDashboardCoreWidgets;
  const showChats =
    !isActivityLogOnly &&
    capabilities.canUseGuestCommunication &&
    isNewChatMessageNotificationEnabled;
  const showRequests = !isActivityLogOnly && capabilities.canUseRequestsSystem;
  const showArrivalsWidget = !isActivityLogOnly;
  const showInHouseWidget = !isActivityLogOnly;
  const showDeparturesWidget = !isActivityLogOnly;
  const showAnalyticsWidget = showDashboardCoreWidgets;
  const showKeyMetricsWidget = showDashboardCoreWidgets;
  const storageKey = `dashboard.widgets.${userProfile?.id ?? "anonymous"}.${
    hotelId ?? "global"
  }`;
  const [widgetPreferences, setWidgetPreferences] =
    useDashboardWidgetPreferences(storageKey);
  const availableWidgetIds = useMemo(() => {
    const ids = new Set<DashboardWidgetId>();
    if (showArrivalsWidget) ids.add("arrivals");
    if (showInHouseWidget) ids.add("inHouse");
    if (showDeparturesWidget) ids.add("departures");
    if (showChats) ids.add("chats");
    if (showRequests) ids.add("requests");
    ids.add("activityLog");
    if (showAnalyticsWidget) ids.add("analytics");
    if (showKeyMetricsWidget) ids.add("keyMetrics");
    return ids;
  }, [
    showAnalyticsWidget,
    showArrivalsWidget,
    showChats,
    showDeparturesWidget,
    showInHouseWidget,
    showKeyMetricsWidget,
    showRequests,
  ]);
  const isWidgetVisible = (id: DashboardWidgetId) =>
    availableWidgetIds.has(id) && widgetPreferences.hidden[id] !== true;
  const topWidgetOrder = widgetPreferences.topOrder.filter(isWidgetVisible);
  const middleWidgetOrder =
    widgetPreferences.middleOrder.filter(isWidgetVisible);
  const bottomWidgetOrder =
    widgetPreferences.bottomOrder.filter(isWidgetVisible);
  const visibleTopWidgetCount = topWidgetOrder.length;
  const topRowColsClass = getTopRowColsClass(visibleTopWidgetCount);
  const bottomRowColSpans = getBottomRowColSpans(
    bottomWidgetOrder.includes("activityLog"),
    bottomWidgetOrder.includes("analytics"),
    bottomWidgetOrder.includes("keyMetrics"),
  );
  const { totalToday, completedTodayCount } = useDashboardGuestRequestMetrics();
  const keyMetrics: DashboardKeyMetricsState = useDashboardKeyMetrics();
  const {
    arrivalsChartData,
    departuresChartData,
    inHouseChartData,
    preCheckinsCount,
    preCheckoutsCount,
  } = useDashboardSettlementHourlyCharts();

  if (userProfile?.role === Role.TENANT_ADMIN) {
    navigate("/hotels");
  }

  return (
    <div
      className={
        "font-patrick flex h-full flex-row leading-normal font-normal tracking-tight text-zinc-900"
      }
    >
      <div
        className={
          "mx-auto flex min-w-0 flex-1 grow flex-col gap-4 overflow-scroll px-5 py-5"
        }
      >
        <div className={"flex flex-row justify-between"}>
          <DashboardGreeting
            className={"flex-1 opacity-70"}
            name={
              userProfile?.profile?.firstName && userProfile?.profile?.lastName
                ? `${userProfile.profile.firstName} ${userProfile.profile.lastName}`
                : userProfile?.profile?.firstName || "User"
            }
            message="How are you today?"
          />
          <div className="flex flex-1 items-start justify-end gap-3">
            <DateTime />
            <DashboardWidgetCustomizer
              preferences={widgetPreferences}
              availableWidgetIds={availableWidgetIds}
              onPreferencesChange={setWidgetPreferences}
            />
          </div>
        </div>
        {visibleTopWidgetCount > 0 && (
          <div
            className={cn(
              "grid h-36 grid-rows-1 gap-x-4 gap-y-5",
              topRowColsClass,
            )}
          >
            {topWidgetOrder.map((widgetId) => {
              if (widgetId === "arrivals") {
                return (
                  <DashboardVidgetContainer
                    key={widgetId}
                    className={dashboardChartVidgetClassName}
                    contentClassName="overflow-visible"
                    title="Arrivals"
                    value={arrivals}
                    icon={<Plane className="text-eathy-comfort-3 size-5" />}
                    badgeNode={
                      <Badge
                        className="bg-royal-glow-5 text-royal-glow-2 border-0 font-medium"
                        text="Pre Check-ins"
                        value={String(preCheckinsCount)}
                      />
                    }
                  >
                    <ArrivalChart
                      metricScale={arrivals}
                      data={arrivalsChartData}
                    />
                  </DashboardVidgetContainer>
                );
              }

              if (widgetId === "inHouse") {
                return (
                  <DashboardVidgetContainer
                    key={widgetId}
                    className={dashboardChartVidgetClassName}
                    contentClassName="overflow-visible"
                    title="In-House"
                    value={inHouse}
                    icon={<Bed className="text-leathy-comfort-3 size-5" />}
                    badgeNode={
                      capabilities.canUseRequestsSystem ? (
                        <Badge
                          className="bg-royal-glow-5 border-0 font-medium text-emerald-900"
                          text="Upsells"
                          value={String(keyMetrics.upsells.value)}
                        />
                      ) : undefined
                    }
                  >
                    <InHouseChart
                      metricScale={inHouse}
                      data={inHouseChartData}
                    />
                  </DashboardVidgetContainer>
                );
              }

              return (
                <DashboardVidgetContainer
                  key={widgetId}
                  className={dashboardChartVidgetClassName}
                  contentClassName="overflow-visible"
                  title="Departures"
                  value={departures}
                  icon={<Briefcase className="text-eathy-comfort-3 size-5" />}
                  badgeNode={
                    <Badge
                      className="bg-royal-glow-5 text-royal-glow-1 border-0 font-medium"
                      text="Pre Check-outs"
                      value={String(preCheckoutsCount)}
                    />
                  }
                >
                  <DepartureChart
                    metricScale={departures}
                    data={departuresChartData}
                  />
                </DashboardVidgetContainer>
              );
            })}
          </div>
        )}
        {middleWidgetOrder.length > 0 && (
          <div
            className={cn("grid h-72 grid-rows-1 gap-x-4 gap-y-5", {
              "grid-cols-2": middleWidgetOrder.length === 2,
              "grid-cols-1": middleWidgetOrder.length === 1,
            })}
          >
            {middleWidgetOrder.map((widgetId) =>
              widgetId === "chats" ? (
                <DashboardVidgetContainer
                  key={widgetId}
                  icon={
                    <MessageCircle className="text-light-stone" aria-hidden />
                  }
                  title="Chats"
                  value={dashboardGuestChatsCount}
                  className="col-span-1 h-full min-h-0 w-full gap-2 px-4 pt-4 pb-px"
                >
                  <DashboardChatsContainer />
                </DashboardVidgetContainer>
              ) : (
                <DashboardVidgetContainer
                  key={widgetId}
                  icon={
                    <ClipboardList className="text-light-stone" aria-hidden />
                  }
                  title={t("requests.dashboard.widgetTitle")}
                  value={totalToday}
                  badgeNode={
                    <Badge
                      text={t("requests.dashboard.completedTodayBadge")}
                      value={String(completedTodayCount)}
                    />
                  }
                  className="col-span-1 h-full min-h-0 w-full gap-2 px-4 pt-4 pb-px"
                >
                  <DashboardRequestsContainer />
                </DashboardVidgetContainer>
              ),
            )}
          </div>
        )}
        {bottomWidgetOrder.length > 0 && (
          <div className={"grid h-52 grid-cols-10 grid-rows-1 gap-x-4 gap-y-5"}>
            {bottomWidgetOrder.map((widgetId) => {
              if (widgetId === "activityLog") {
                return (
                  <DashboardVidgetContainer
                    key={widgetId}
                    icon={<Activity className="text-light-stone" aria-hidden />}
                    title="Activity Log"
                    className={cn(
                      "h-full min-h-0 w-full gap-2 px-4 pt-4 pb-px",
                      bottomRowColSpans.activityLog,
                    )}
                  >
                    <ActivityLogContainer
                      showCoreFeatures={showDashboardCoreWidgets}
                    />
                  </DashboardVidgetContainer>
                );
              }

              if (widgetId === "analytics") {
                return (
                  <DashboardVidgetContainer
                    key={widgetId}
                    icon={
                      <LineChart className="stroke-light-stone" aria-hidden />
                    }
                    title="Analytics"
                    className={cn(
                      "h-full min-h-0 w-full gap-2 px-4 pt-4 pb-3",
                      bottomRowColSpans.analytics,
                    )}
                    contentClassName="overflow-visible"
                    endNode={
                      <Select
                        value={analyticsPeriod}
                        onValueChange={(v) =>
                          setAnalyticsPeriod(v as PeriodType)
                        }
                      >
                        <SelectTrigger className="w-auto min-w-[100px] border-0 bg-transparent text-xs font-medium shadow-none">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7d">7 days</SelectItem>
                          <SelectItem value="14d">14 days</SelectItem>
                          <SelectItem value="1m">1 month</SelectItem>
                          <SelectItem value="1y">1 year</SelectItem>
                        </SelectContent>
                      </Select>
                    }
                  >
                    <DashboardAnalytics
                      periodType={analyticsPeriod}
                      showGuestSatisfaction={hasFeedbacksAccess}
                      showUpsells={capabilities.canUseRequestsSystem}
                    />
                  </DashboardVidgetContainer>
                );
              }

              return (
                <DashboardVidgetContainer
                  key={widgetId}
                  icon={
                    <LayoutDashboard className="text-light-stone" aria-hidden />
                  }
                  title="Key Metrics"
                  className={cn(
                    "h-full min-h-0 w-full gap-2 px-4 pt-4 pb-3",
                    bottomRowColSpans.keyMetrics,
                  )}
                  contentClassName="overflow-visible"
                >
                  <DashboardKeyMetrics
                    items={[
                      {
                        label: "Occupancy",
                        value: keyMetrics.occupancy.value,
                        secondary: keyMetrics.occupancy.secondary,
                        secondaryClassName:
                          keyMetrics.occupancy.secondaryClassName,
                      },
                      ...(hasFeedbacksAccess
                        ? [
                            {
                              label: "Guest Satisfaction",
                              value: keyMetrics.guestSatisfaction.value,
                              secondary: keyMetrics.guestSatisfaction.secondary,
                              valueClassName:
                                keyMetrics.guestSatisfaction.valueClassName,
                              secondaryClassName:
                                keyMetrics.guestSatisfaction.secondaryClassName,
                            },
                          ]
                        : []),
                      ...(capabilities.canUseRequestsSystem
                        ? [
                            {
                              label: "Upsells",
                              value: keyMetrics.upsells.value,
                              secondary: keyMetrics.upsells.secondary,
                              secondaryClassName:
                                keyMetrics.upsells.secondaryClassName,
                            },
                          ]
                        : []),
                    ]}
                  />
                </DashboardVidgetContainer>
              );
            })}
          </div>
        )}
      </div>
      <motion.div
        initial={false}
        animate={{
          width: guestExperienceOpen ? "13rem" : "2.5rem",
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut",
        }}
        className="flex h-full min-h-0 shrink-0 flex-col overflow-hidden"
      >
        {guestExperienceOpen ? (
          <SidebarPanel className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-y-auto pb-0">
            <GuestExperienceSidebar
              className="min-h-0 flex-1 bg-black/2 pt-0"
              onClosePanel={() => setGuestExperienceOpen(false)}
            />
          </SidebarPanel>
        ) : (
          <div className="flex h-full w-full min-w-0 flex-col pt-4">
            <Button
              type="button"
              variant="new_ghost"
              size="icon_sm"
              className="!bg-transparent text-neutral-600"
              aria-label="Open guest experience panel"
              onClick={() => setGuestExperienceOpen(true)}
            >
              <ArrowLeftToLine className="size-5" aria-hidden />
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
};
